import React from "react";
import ConfirmationModal from "./ConfirmationModal";
import styles from "./Dashboard.module.css";
import {
  datasetCoverageApi,
  AgentDatasetPair,
  LangfuseDatasetRow,
} from "../services/datasetCoverageApi";
import {
  buildLangfuseDatasetUrl,
  getLangfuseDefaultProjectId,
  getLangfuseUiBase,
} from "../utils/langfuseUrls";

type Props = {
  agentId: string | null;
  isCreatingAgent: boolean;
};

const AgentDatasetCoverage: React.FC<Props> = ({ agentId, isCreatingAgent }) => {
  const [datasets, setDatasets] = React.useState<LangfuseDatasetRow[]>([]);
  const [pairs, setPairs] = React.useState<AgentDatasetPair[]>([]);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [busyKey, setBusyKey] = React.useState<string | null>(null);
  const [pendingNightly, setPendingNightly] = React.useState<Record<string, boolean>>({});
  const [removeTarget, setRemoveTarget] = React.useState<{ datasetId: string; label: string } | null>(
    null,
  );

  const loadPairs = React.useCallback(async () => {
    if (!agentId) return;
    const res = await datasetCoverageApi.listPairsForAgent(agentId);
    setPairs(res.data);
  }, [agentId]);

  const loadDatasetsPage = React.useCallback(
    async (nextPage: number, append: boolean) => {
      setLoading(true);
      setError(null);
      try {
        const res = await datasetCoverageApi.listDatasets({ page: nextPage, limit: 50 });
        setDatasets((prev) => (append ? [...prev, ...res.data] : res.data));
        const tp = res.meta?.totalPages;
        setTotalPages(typeof tp === "number" && tp > 0 ? tp : nextPage);
        setPage(nextPage);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Failed to load datasets.");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  React.useEffect(() => {
    if (!agentId || isCreatingAgent) {
      setDatasets([]);
      setPairs([]);
      setError(null);
      setPage(1);
      setTotalPages(1);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const prRes = await datasetCoverageApi.listPairsForAgent(agentId);
        if (cancelled) return;
        setPairs(prRes.data);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load agent-dataset pairs.");
      }
      try {
        const dsRes = await datasetCoverageApi.listDatasets({ page: 1, limit: 50 });
        if (cancelled) return;
        setDatasets(dsRes.data);
        const tp = dsRes.meta?.totalPages;
        setTotalPages(typeof tp === "number" && tp > 0 ? tp : 1);
        setPage(1);
      } catch (e: unknown) {
        if (!cancelled) {
          setError((prev) => prev || (e instanceof Error ? e.message : "Failed to load datasets from Langfuse."));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [agentId, isCreatingAgent]);

  const pairByDatasetId = React.useMemo(() => {
    const m = new Map<string, AgentDatasetPair>();
    pairs.forEach((p) => m.set(p.dataset_id, p));
    return m;
  }, [pairs]);

  const datasetIds = React.useMemo(() => new Set(datasets.map((d) => d.id)), [datasets]);

  const orphanPairs = React.useMemo(
    () => pairs.filter((p) => !datasetIds.has(p.dataset_id)),
    [pairs, datasetIds],
  );

  const handleConnect = async (row: LangfuseDatasetRow) => {
    if (!agentId) return;
    const nightly = pendingNightly[row.id] ?? false;
    setBusyKey(`connect:${row.id}`);
    setError(null);
    try {
      await datasetCoverageApi.connectPair({
        agent_id: agentId,
        dataset_id: row.id,
        dataset_name: row.name,
        nightly,
      });
      await loadPairs();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to connect dataset.");
    } finally {
      setBusyKey(null);
    }
  };

  const handleNightlyChange = async (datasetId: string, nightly: boolean, datasetName: string | null) => {
    if (!agentId) return;
    const existing = pairByDatasetId.get(datasetId);
    setBusyKey(`night:${datasetId}`);
    setError(null);
    try {
      await datasetCoverageApi.connectPair({
        agent_id: agentId,
        dataset_id: datasetId,
        dataset_name: datasetName ?? existing?.dataset_name ?? null,
        nightly,
      });
      await loadPairs();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to update nightly flag.");
    } finally {
      setBusyKey(null);
    }
  };

  const confirmRemove = async () => {
    if (!agentId || !removeTarget) return;
    setBusyKey(`remove:${removeTarget.datasetId}`);
    setError(null);
    try {
      await datasetCoverageApi.removePair(agentId, removeTarget.datasetId);
      setRemoveTarget(null);
      await loadPairs();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to remove dataset.");
    } finally {
      setBusyKey(null);
    }
  };

  const langfuseUiBase = React.useMemo(() => getLangfuseUiBase(), []);
  const langfuseDefaultProjectId = React.useMemo(() => getLangfuseDefaultProjectId(), []);

  const renderRow = (args: {
    langfuse?: LangfuseDatasetRow;
    id: string;
    name: string;
    description?: string | null;
    projectId?: string | null;
    pair: AgentDatasetPair | undefined;
    source: "langfuse" | "persisted";
  }) => {
    const { langfuse, id, name, description, projectId, pair, source } = args;
    const connected = Boolean(pair);
    const nightlyPending = pendingNightly[id] ?? false;
    const resolvedProjectId = projectId ?? langfuse?.projectId ?? langfuseDefaultProjectId;
    const langfuseDatasetUrl =
      langfuseUiBase && resolvedProjectId && id
        ? buildLangfuseDatasetUrl(langfuseUiBase, resolvedProjectId, id)
        : null;

    return (
      <div key={`${source}-${id}`} className={styles.agentsCoverageRow}>
        <div className={styles.agentsCoverageMain}>
          <div className={styles.agentsCoverageNameRow}>
            {langfuseDatasetUrl ? (
              <a
                href={langfuseDatasetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.agentsCoverageNameLink}
              >
                <strong className={styles.agentsCoverageName}>{name}</strong>
              </a>
            ) : (
              <strong className={styles.agentsCoverageName}>{name}</strong>
            )}
            {langfuseDatasetUrl ? (
              <a
                href={langfuseDatasetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.agentsCoverageLangfuseLink}
              >
                View in Langfuse
              </a>
            ) : null}
          </div>
          <span className={styles.agentsCoverageId}>{id}</span>
          {description ? <p className={styles.agentsCoverageDesc}>{description}</p> : null}
          {source === "persisted" ? (
            <p className={styles.agentsCoverageOrphanNote}>
              Still monitored here, but this dataset is no longer listed in Langfuse.
            </p>
          ) : null}
        </div>
        <div className={styles.agentsCoverageActions}>
          <span
            className={
              connected ? `${styles.agentsCoverageBadge} ${styles.agentsCoverageBadgeOn}` : styles.agentsCoverageBadge
            }
          >
            {connected ? "Connected" : "Not connected"}
          </span>
          {connected ? (
            <>
              <label className={styles.agentsCoverageCheck}>
                <input
                  type="checkbox"
                  checked={pair!.nightly}
                  disabled={busyKey !== null}
                  onChange={(e) => handleNightlyChange(id, e.target.checked, pair!.dataset_name)}
                />
                Nightly monitoring
              </label>
              <button
                type="button"
                className={styles.metricsDangerBtn}
                disabled={busyKey !== null}
                onClick={() =>
                  setRemoveTarget({
                    datasetId: id,
                    label: pair!.dataset_name || name,
                  })
                }
              >
                Disconnect
              </button>
            </>
          ) : (
            <>
              <label className={styles.agentsCoverageCheck}>
                <input
                  type="checkbox"
                  checked={nightlyPending}
                  disabled={busyKey !== null}
                  onChange={(e) =>
                    setPendingNightly((prev) => ({
                      ...prev,
                      [id]: e.target.checked,
                    }))
                  }
                />
                Enable nightly when connecting
              </label>
              <button
                type="button"
                className={styles.metricsSecondaryBtn}
                disabled={busyKey !== null}
                onClick={() => {
                  if (langfuse) void handleConnect(langfuse);
                }}
              >
                {busyKey === `connect:${id}` ? "Connecting…" : "Connect"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (isCreatingAgent || !agentId) {
    return (
      <section className={styles.agentsCoverageSection} aria-labelledby="agents-coverage-heading">
        <h3 id="agents-coverage-heading" className={styles.agentsCoverageTitle}>
          Dataset coverage
        </h3>
        <p className={styles.agentsCoverageHint}>
          Connect Langfuse datasets to this agent for monitoring coverage and optional nightly runs. Save the agent
          first, then pick datasets from your Langfuse project.
        </p>
        <div className={styles.agentsCoverageEmpty}>
          <p>Dataset linking is available after the agent is created.</p>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.agentsCoverageSection} aria-labelledby="agents-coverage-heading">
      <h3 id="agents-coverage-heading" className={styles.agentsCoverageTitle}>
        Dataset coverage
      </h3>
      <p className={styles.agentsCoverageHint}>
        Discover Langfuse datasets, connect them to this agent, and toggle nightly monitoring. Changes apply
        immediately.
      </p>

      {loading && datasets.length === 0 && !error ? (
        <p className={styles.agentsCoverageLoading}>Loading datasets…</p>
      ) : null}

      {error ? (
        <div className={`${styles.metricsErrorBox} ${styles.agentsCoverageMessage}`}>
          <p>{error}</p>
        </div>
      ) : null}

      {!loading && !error && datasets.length === 0 && orphanPairs.length === 0 ? (
        <div className={styles.agentsCoverageEmpty}>
          <p>No Langfuse datasets found. Add datasets in Langfuse or check integration settings.</p>
        </div>
      ) : null}

      <div className={styles.agentsCoverageList}>
        {datasets.map((d) =>
          renderRow({
            langfuse: d,
            id: d.id,
            name: d.name,
            description: d.description,
            projectId: d.projectId,
            pair: pairByDatasetId.get(d.id),
            source: "langfuse",
          }),
        )}
        {orphanPairs.map((p) =>
          renderRow({
            id: p.dataset_id,
            name: p.dataset_name || "Dataset (removed from Langfuse)",
            description: null,
            pair: p,
            source: "persisted",
          }),
        )}
      </div>

      {page < totalPages ? (
        <div className={styles.agentsCoverageMore}>
          <button
            type="button"
            className={styles.metricsSecondaryBtn}
            disabled={loading || busyKey !== null}
            onClick={() => void loadDatasetsPage(page + 1, true)}
          >
            {loading ? "Loading…" : "Load more datasets"}
          </button>
        </div>
      ) : null}

      <ConfirmationModal
        isOpen={removeTarget !== null}
        onClose={() => {
          if (!busyKey) setRemoveTarget(null);
        }}
        onConfirm={() => void confirmRemove()}
        title="Disconnect dataset"
        message={
          removeTarget
            ? `Stop monitoring "${removeTarget.label}" for this agent? You can connect it again later.`
            : ""
        }
        confirmText="Disconnect"
        cancelText="Cancel"
        isDestructive
        loading={Boolean(removeTarget && busyKey === `remove:${removeTarget.datasetId}`)}
      />
    </section>
  );
};

export default AgentDatasetCoverage;
